import React, { useState, useEffect } from 'react';
import { supabase } from '../../../utils/supabase';

const ActivityFeed = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchRecentActivities();
  }, []);

  const fetchRecentActivities = async () => {
    try {
      setLoading(true);
      
      // Fetch recent user registrations
      const { data: users, error: usersError } = await supabase
        .from('user_profiles')
        .select('id, full_name, email, created_at')
        .order('created_at', { ascending: false })
        .limit(10);

      if (usersError) throw usersError;

      // Fetch recent contacts/support tickets
      const { data: contacts, error: contactsError } = await supabase
        .from('contacts')
        .select(`
          id, 
          subject, 
          issue_type, 
          status, 
          created_at,
          user_profiles!contacts_user_id_fkey(full_name, email)
        `)
        .order('created_at', { ascending: false })
        .limit(10);

      if (contactsError) throw contactsError;

      // Fetch recent email logs
      const { data: emails, error: emailsError } = await supabase
        .from('email_logs')
        .select('id, recipient_email, template_type, status, created_at')
        .order('created_at', { ascending: false })
        .limit(10);

      if (emailsError) throw emailsError;

      // Combine and format activities
      const combinedActivities = [
        ...users.map(user => ({
          id: `user-${user.id}`,
          type: 'user_registration',
          title: 'New User Registration',
          description: `${user.full_name || user.email} joined the platform`,
          timestamp: user.created_at,
          icon: '👤',
          color: 'text-green-400'
        })),
        ...contacts.map(contact => ({
          id: `contact-${contact.id}`,
          type: 'support_ticket',
          title: 'New Support Ticket',
          description: `${contact.user_profiles?.full_name || 'User'} - ${contact.subject}`,
          timestamp: contact.created_at,
          icon: '🎫',
          color: 'text-blue-400',
          status: contact.status
        })),
        ...emails.map(email => ({
          id: `email-${email.id}`,
          type: 'email_sent',
          title: 'Email Sent',
          description: `${email.template_type} email to ${email.recipient_email}`,
          timestamp: email.created_at,
          icon: '📧',
          color: email.status === 'sent' ? 'text-green-400' : 'text-yellow-400',
          status: email.status
        }))
      ];

      // Sort by timestamp and take the most recent 15
      const sortedActivities = combinedActivities
        .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
        .slice(0, 15);

      setActivities(sortedActivities);
    } catch (err) {
      console.error('Error fetching activities:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffInMinutes = Math.floor((now - time) / (1000 * 60));

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      'open': 'bg-blue-500/10 text-blue-400 border-blue-500/20',
      'in_progress': 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
      'resolved': 'bg-green-500/10 text-green-400 border-green-500/20',
      'closed': 'bg-gray-500/10 text-gray-400 border-gray-500/20',
      'sent': 'bg-green-500/10 text-green-400 border-green-500/20',
      'delivered': 'bg-blue-500/10 text-blue-400 border-blue-500/20',
      'failed': 'bg-red-500/10 text-red-400 border-red-500/20',
      'pending': 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20'
    };

    const colorClass = statusColors[status] || 'bg-gray-500/10 text-gray-400 border-gray-500/20';

    return (
      <span className={`px-2 py-1 text-xs rounded-full border ${colorClass}`}>
        {status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Recent Activity</h3>
        <div className="space-y-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="flex items-start space-x-3 animate-pulse">
              <div className="w-8 h-8 bg-muted rounded-full"></div>
              <div className="flex-1">
                <div className="h-4 bg-muted rounded w-3/4 mb-2"></div>
                <div className="h-3 bg-muted rounded w-1/2"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Recent Activity</h3>
        <div className="text-center py-8">
          <div className="text-red-400 text-4xl mb-2">⚠️</div>
          <p className="text-muted-foreground">Failed to load activity feed</p>
          <button
            onClick={fetchRecentActivities}
            className="mt-4 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-foreground">Recent Activity</h3>
        <button
          onClick={fetchRecentActivities}
          className="text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          Refresh
        </button>
      </div>

      <div className="space-y-4 max-h-96 overflow-y-auto">
        {activities.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-muted-foreground text-4xl mb-2">📊</div>
            <p className="text-muted-foreground">No recent activity</p>
          </div>
        ) : (
          activities.map((activity) => (
            <div key={activity.id} className="flex items-start space-x-3 p-3 rounded-lg hover:bg-muted/50 transition-colors">
              <div className="text-2xl">{activity.icon}</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-medium text-foreground truncate">
                    {activity.title}
                  </h4>
                  <span className="text-xs text-muted-foreground ml-2 flex-shrink-0">
                    {formatTimeAgo(activity.timestamp)}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground truncate mt-1">
                  {activity.description}
                </p>
                {activity.status && (
                  <div className="mt-2">
                    {getStatusBadge(activity.status)}
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {activities.length > 0 && (
        <div className="mt-4 pt-4 border-t border-border">
          <button className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors">
            View All Activity →
          </button>
        </div>
      )}
    </div>
  );
};

export default ActivityFeed;
